import { useCallback, useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import StarIcon from '@mui/icons-material/Star'
import { useDispatch, useSelector } from 'react-redux'
import { openFeedbackPanel } from '../../reducer/feedback.reducer'
import { selectFeedbackStore } from '../../selectors/feedback'

/** Matches `height` on the trigger so clamp keeps the full tab in the viewport. */
const TRIGGER_HEIGHT_PX = 100

function clampCenterY(y: number) {
    const half = TRIGGER_HEIGHT_PX / 2
    if (typeof window === 'undefined') return y
    return Math.min(window.innerHeight - half, Math.max(half, y))
}

export function FeedbackTriggerButton() {
    const dispatch = useDispatch()
    const { isExpanded, isClosing, isCapturing } = useSelector(selectFeedbackStore)
    const feedbackUiOpen = isExpanded || isClosing || isCapturing

    /** Vertical center of the tab in viewport px; `null` uses CSS `top: 50%`. */
    const [centerY, setCenterY] = useState<number | null>(null)

    const dragSession = useRef<{
        pointerId: number
        pointerDownY: number
        centerAtDown: number
        maxAbsDelta: number
    } | null>(null)

    useEffect(() => {
        if (centerY === null) return
        const onResize = () => setCenterY(c => (c == null ? c : clampCenterY(c)))
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [centerY])

    const handlePointerDown = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
        if (e.button !== 0) return
        e.currentTarget.setPointerCapture(e.pointerId)
        const rect = e.currentTarget.getBoundingClientRect()
        const currentCenter = centerY ?? rect.top + rect.height / 2
        dragSession.current = {
            pointerId: e.pointerId,
            pointerDownY: e.clientY,
            centerAtDown: currentCenter,
            maxAbsDelta: 0
        }
    }, [centerY])

    const handlePointerMove = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
        const s = dragSession.current
        if (!s || e.pointerId !== s.pointerId) return
        const delta = e.clientY - s.pointerDownY
        s.maxAbsDelta = Math.max(s.maxAbsDelta, Math.abs(delta))
        setCenterY(clampCenterY(s.centerAtDown + delta))
    }, [])

    const finishPointer = useCallback(
        (e: React.PointerEvent<HTMLButtonElement>) => {
            const s = dragSession.current
            if (!s || e.pointerId !== s.pointerId) return
            if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                e.currentTarget.releasePointerCapture(e.pointerId)
            }
            dragSession.current = null
            const TAP_MOVE_THRESHOLD_PX = 8
            if (s.maxAbsDelta < TAP_MOVE_THRESHOLD_PX) {
                dispatch(openFeedbackPanel())
            }
        },
        [dispatch]
    )

    const handleLostPointerCapture = useCallback(() => {
        dragSession.current = null
    }, [])

    if (feedbackUiOpen) {
        return null
    }

    return (
        <Box
            component="button"
            type="button"
            aria-label="Open feedback"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={finishPointer}
            onPointerCancel={finishPointer}
            onLostPointerCapture={handleLostPointerCapture}
            sx={{
                position: 'fixed',
                right: 0,
                top: centerY == null ? '50%' : `${centerY}px`,
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 25,
                height: TRIGGER_HEIGHT_PX,
                py: 0,
                px: 0,
                m: 0,
                border: 'none',
                cursor: 'pointer',
                touchAction: 'none',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                borderTopLeftRadius: 8,
                borderBottomLeftRadius: 8,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                boxShadow: 2,
                transform: 'translateY(-50%)',
                '&:active': {
                    cursor: 'grabbing'
                },
                '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.light',
                    outlineOffset: 2
                }
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 0.5,
                    transform: 'rotate(-90deg)',
                    whiteSpace: 'nowrap',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    fontFamily: 'inherit',
                    letterSpacing: '0.02em'
                }}
            >
                <StarIcon sx={{ fontSize: 12, marginLeft: '-5px' }} aria-hidden />
                Feedback
            </Box>
        </Box>
    )
}
